// React, the library's own barrel, and the EnvironmentContext stay static —
// React is everywhere in the host app already, and the library barrel is
// the small surface the LLM imports from `@envision/ui-library`. The three
// big namespaces (@mui/material, @mui/icons-material, recharts) move into
// `loadHeavyModules` so Vite code-splits them into chunks fetched only when
// the first liveTsx block of a session mounts — the splash, login, and
// dashboards list never pay that ~5 MB cost.
//
// VizLibrary used to live in this package and was statically spread into
// every liveTsx scope. It now lives in dashboard-app (and is hand-curated
// in the LLM template). Hosts inject it at startup via `extendDefaultScope`
// + `extendModuleRegistry` — see dashboard-app/client/src/registerModules.ts.
import * as React from 'react';
import * as UILibrary from '../index'
import { useEnvironment as useEnvironmentDirect } from './EnvironmentContext';

/**
 * Module registry mapping ES module specifiers to pre-loaded objects.
 * Used by the import pre-processor in LiveJsxBlock to resolve
 * `import { X } from 'module'` into scope bindings at runtime.
 *
 * Built lazily (inside the function) to avoid circular-dependency issues:
 * jsxScope.ts → ../index → ./LlmResponseRender → jsxScope.ts
 */


const GLOBAL_KEY = Symbol.for('envision:module-registry-extensions');

function getRegistryExtensions(): Record<string, Record<string, unknown>> {
    const g = globalThis as Record<symbol, unknown>;
    if (!g[GLOBAL_KEY]) g[GLOBAL_KEY] = {};
    return g[GLOBAL_KEY] as Record<string, Record<string, unknown>>;
}

/**
 * Extend the module registry with additional exports.
 * Call once at app startup to inject components that were moved out of the library
 * but are still imported by LLM-generated code under `@envision/ui-library`.
 *
 * Extensions are stored on globalThis so they survive Vite HMR reloads
 * (module-level variables would be reset when this file is re-evaluated).
 *
 * @example
 * ```ts
 * import { extendModuleRegistry } from '@envision/ui-library/components';
 * import { ExamplePad, ExampleCardSketh, PrototypePad } from '@app/components/LlmComponents';
 *
 * extendModuleRegistry('@envision/ui-library', { ExamplePad, ExampleCardSketh, PrototypePad });
 * ```
 */
export function extendModuleRegistry(specifier: string, exports: Record<string, unknown>): void {
    const ext = getRegistryExtensions();
    ext[specifier] = {
        ...(ext[specifier] ?? {}),
        ...exports,
    };
}

const SCOPE_KEY = Symbol.for('envision:default-scope-extensions');

function getScopeExtensions(): Record<string, unknown> {
    const g = globalThis as Record<symbol, unknown>;
    if (!g[SCOPE_KEY]) g[SCOPE_KEY] = {};
    return g[SCOPE_KEY] as Record<string, unknown>;
}

/**
 * Extend the bare-identifier scope injected into every compiled liveTsx
 * block. Use this for components/values that the LLM is allowed to reference
 * without an explicit `import` (e.g. VizLibrary's Chart/Metric/ContainTable).
 *
 * Extensions are stored on globalThis so they survive Vite HMR reloads.
 *
 * @example
 * ```ts
 * import { extendDefaultScope } from '@envision/ui-library/components';
 * import * as VizLibrary from '@app/components/VizLibrary';
 * extendDefaultScope(VizLibrary);
 * ```
 */
export function extendDefaultScope(exports: Record<string, unknown>): void {
    const ext = getScopeExtensions();
    Object.assign(ext, exports);
}

/**
 * Cached single-flight promise for the heavy namespace imports. The first
 * call kicks off the chunk fetches; every subsequent call returns the same
 * resolved promise instantly. Survives Vite HMR because module-level let
 * is reset only when this file is re-evaluated, and that re-evaluation
 * naturally re-warms the cache on the next call.
 */
let heavyModulesPromise: Promise<{
    MuiMaterial: typeof import('@mui/material');
    Icons: typeof import('@mui/icons-material');
    Recharts: typeof import('recharts');
}> | null = null;

function loadHeavyModules() {
    if (!heavyModulesPromise) {
        heavyModulesPromise = Promise.all([
            import('@mui/material'),
            import('@mui/icons-material'),
            import('recharts'),
        ]).then(([MuiMaterial, Icons, Recharts]) => ({ MuiMaterial, Icons, Recharts }));
    }
    return heavyModulesPromise;
}

export async function getModuleRegistry(): Promise<Record<string, Record<string, unknown>>> {
    const { MuiMaterial, Icons, Recharts } = await loadHeavyModules();

    const base: Record<string, Record<string, unknown>> = {
        'react': { ...React, default: React } as Record<string, unknown>,
        '@mui/material': { ...MuiMaterial } as Record<string, unknown>,
        '@mui/material/styles': { ...MuiMaterial } as Record<string, unknown>,
        '@mui/icons-material': { ...Icons } as Record<string, unknown>,
        'recharts': { ...Recharts } as Record<string, unknown>,
        '@envision/ui-library': {
            ...UILibrary,
            useEnvironment: useEnvironmentDirect,
        } as Record<string, unknown>,
    };

    for (const [specifier, exports] of Object.entries(getRegistryExtensions())) {
        base[specifier] = { ...(base[specifier] ?? {}), ...exports };
    }

    return base;
}

/**
 * Base scope injected into every compiled LiveJSX block.
 *
 * React is always present because Sucrase classic JSX emits
 * React.createElement() calls. JS globals are included because
 * `new Function` code does not see the outer lexical environment.
 */
export function getDefaultJsxScope(): Record<string, unknown> {
    const basics = {
        React,
        // JS globals (sandboxed code loses implicit access to these)
        console, Math, JSON, Date,
        Array, Object, parseInt, parseFloat,
        String, Number, Boolean, Map, Set,
        Promise, RegExp, Error, Symbol,
        setTimeout, clearTimeout, setInterval, clearInterval,
        encodeURIComponent, decodeURIComponent, encodeURI, decodeURI,
        isNaN, isFinite, undefined, NaN, Infinity,
    }
    return { ...basics, ...getScopeExtensions() };
};
